import React, { useEffect, useState } from 'react';
import { Table, Upload, Progress } from 'antd';
import { CheckCircleFilled, DeleteOutlined } from '@ant-design/icons';
import uploadIcon from '../../assets/icons/upload-icon.svg';
import DownloadCsvIcon from '../../assets/icons/download-csv-icon.svg';
import Button from '../../components/Button/Button';
import csvIcon from '../../assets/icons/csv-icon.svg';
import { processExcelFile } from '../../utils/processExcelFile';
import openNotification from '../../components/Common/OpenNotification';
import { useDispatch, useSelector } from 'react-redux';
import { checkTablesUploadStatus, fetchDownloadExcel, fetchDownloadExcelForAdmin, getProjectStatus, uploadExcelData, uploadPmgProjectsBulk } from '../../redux/budgetSlice/budgetSlice';
import Loader from '../../components/Common/LoadingSpinner/LoadingSpinner';
import { Lock, AlertCircle } from "lucide-react";


const { Dragger } = Upload;

const defaultData = [
        { key: '1', title: 'Project Cost', fileName: 'project_cost.csv' },
        {
                key: '2',
                title: 'Other Apportioned Cost',
                fileName: 'other_apportioned_cost.csv',
        },
        {
                key: '3',
                title: 'Other Operational Income',
                fileName: 'other_operational_income.csv',
        },
        { key: '4', title: 'Billing', fileName: 'billing.csv' },
        { key: '5', title: 'Cash Inflow', fileName: 'cash_inflow.csv' },
        { key: '6', title: 'Cash Outflow', fileName: 'cash_outflow.csv' },
];

const statusKeyMap = {
        'project_cost.csv': 'projectCost',
        'other_apportioned_cost.csv': 'apportionedCost',
        'other_operational_income.csv': 'operationalIncome',
        'billing.csv': 'billing',
        'cash_inflow.csv': 'cashInflow',
        'cash_outflow.csv': 'cashOutflow',
        'operating_performance.csv': 'operatingPerformance',
        'notes_to_operating.csv': 'notesOperating',
        'personnel_payments.csv': 'personnelPayment',
        'miscellaneous_expenses.csv': 'misExpenses',
        'direct_order_input.csv': 'directOrder',
        'direct_order_other_apportioned_cost.csv': 'directOrderOA',
        'iut_input.csv': 'iut',
        'iut_other_apportioned_cost.csv': 'iutOA',
        'anticipated_order_input.csv': 'anticipatedOrder',
        'anticipated_order_other_apportioned_cost.csv': 'anticipatedOrderOA',
        'other_projects_project_cost_direct_material.csv': 'opProjectCostDm',
        'other_projects_project_cost_conversion_cost.csv': 'opProjectCostC',
        'other_projects_other_apportioned_cost.csv': 'apportionedCost',
        'other_projects_other_operational_income.csv': 'operationalIncome',
        'other_projects_gross_billing.csv': 'grossBilling',
        'other_projects_net_billing.csv': 'netBilling',
        'other_projects_cash_inflow.csv': 'cashInflow',
        'other_projects_cash_outflow.csv': 'cashOutflow',
        'Project': 'project',
        'project_milestone': 'projectMilestone',
        'project_milestone_mapping': 'projectMilestoneMapping',
};

const CsvUploadTable = ({ tableData, ioNo = '', revNo = '', flowName = '', versionButtons = true }) => {
        const showVersionButtons = Boolean(versionButtons);
        const [uploadProgress, setUploadProgress] = useState({});
        const [uploadedFiles, setUploadedFiles] = useState({});
        const dispatch = useDispatch();
        const { uploadTablesStatus, loading, projectStatus } = useSelector((state) => state.budget);

        const { user } = useSelector((state) => state.auth);
        const isUploadLocked = user?.IS_UPLOAD_LOCKED;

        const isAllFinalSubmitted = Object.values(projectStatus || {}).every((status) => status === 'FINAL_SUBMITTED');
        const role = sessionStorage.getItem('role');

        const handleDownload = (fileName, version) => {
                const fileType = fileName.replace('.csv', '').replace(/ /g, '_');
                dispatch(fetchDownloadExcel({ fileType, ioNo, revNo, version }));
        };
        const handleAdminFileDownload = (fileName) => {
                dispatch(fetchDownloadExcelForAdmin(fileName));
        };

        const handleDelete = (rowKey) => {
                setUploadProgress((prev) => ({ ...prev, [rowKey]: undefined }));
                setUploadedFiles((prev) => ({ ...prev, [rowKey]: undefined }));
        };

        const getUploadProps = (rowKey, fileName) => ({
                name: 'file',
                multiple: false,
                accept: '.csv, .xlsx',
                beforeUpload: (file) => {
                        const isExcel = file.type === 'text/csv' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                        if (!isExcel) {
                                openNotification('error', 'Invalid File Type', 'You can only upload CSV or Excel (.xlsx) files');
                                return Upload.LIST_IGNORE;
                        }

                        const expectedBase = fileName.replace('.csv', '')?.toLowerCase();
                        const uploadedFileName = file.name?.toLowerCase();

                        if (!uploadedFileName.includes(expectedBase) || !uploadedFileName.includes(ioNo.toLocaleLowerCase())) {
                                if (flowName) {
                                        openNotification('error', 'Incorrect File', `Please upload a file that includes "${expectedBase.split('_').join(' ').toUpperCase()}" in the name.`);
                                } else {
                                        openNotification('error', 'Incorrect File', `Please upload a file that includes IO no this ${ioNo} & "${expectedBase.split('_').join(' ').toUpperCase()}" in the name.`);
                                }
                                return Upload.LIST_IGNORE;
                        }

                        return true;
                },
                customRequest: async ({ file, onSuccess, onProgress }) => {
                        setUploadedFiles((prev) => ({
                                ...prev,
                                [rowKey]: { name: file.name, size: file.size },
                        }));

                        let percent = 0;
                        const interval = setInterval(() => {
                                percent += 20;
                                setUploadProgress((prev) => ({ ...prev, [rowKey]: percent }));
                                onProgress({ percent });

                                if (percent >= 100) {
                                        clearInterval(interval);
                                        (async () => {
                                                try {
                                                        const data = await processExcelFile(file);
                                                        const fileType = fileName.replace('.csv', '').replace(/ /g, '_');
                                                        const payload = { ioNo, revNo, fileType, data };
                                                        const normFileType = fileType?.toString().toLowerCase();

                                                        if (normFileType === 'project') {
                                                                const transformed = (data || []).map((row) => ({
                                                                        PID: row.PID ?? row.pid ?? row.Pid ?? row['PID'] ?? '',
                                                                        PROJ_NAME: row.PROJ_NAME ?? row.proj_name ?? row['Project Name'] ?? row['PROJ_NAME'] ?? '',
                                                                        ZERO_DATE: row.ZERO_DATE ?? row.zero_date ?? row['ZERO_DATE'] ?? '',
                                                                        MW: Number(row.MW ?? row.mw ?? 0) || 0,
                                                                        UNIT_NO: row.UNIT_NO ?? row.unit_no ?? '',
                                                                        PTYPE: row.PTYPE ?? row.ptype ?? '',
                                                                        SUB_TYPE: row.SUB_TYPE ?? row.sub_type ?? '',
                                                                        IO_NO: row.IO_NO ?? ioNo,
                                                                        REV_NO: row.REV_NO ?? revNo,
                                                                }));

                                                                await dispatch(uploadPmgProjectsBulk({ data: transformed, fileType }));
                                                                // await dispatch(checkTablesUploadStatus({ IO_NO: ioNo, REV_NO: revNo, flowName }));
                                                                onSuccess('Upload complete');
                                                        } else if (normFileType === 'project_milestone') {
                                                                const transformed = (data || []).map((row) => ({
                                                                        MS_CODE: row.MS_CODE ?? row.ms_code ?? row['MS_CODE'] ?? row['Ms Code'] ?? '',
                                                                        TYPE: row.TYPE ?? row.type ?? '',
                                                                        SUB_TYPE: row.SUB_TYPE ?? row.sub_type ?? '',
                                                                        PKG: row.PKG ?? row.pkg ?? row['PKG'] ?? '',
                                                                        MS_DESC: row.MS_DESC ?? row.ms_desc ?? row['MS_DESC'] ?? row['Milestone Description'] ?? '',
                                                                        SORT_ORDER: Number(row.SORT_ORDER ?? row.sort_order ?? row['SORT_ORDER'] ?? "0") || "0",
                                                                        IS_MONITORING: Number(row.IS_MONITORING ?? row.is_monitoring ?? row['IS_MONITORING'] ?? "0") || "0",
                                                                        IO_NO: row.IO_NO ?? ioNo,
                                                                        REV_NO: row.REV_NO ?? revNo,
                                                                }));

                                                                await dispatch(uploadPmgProjectsBulk({ data: transformed, fileType }));
                                                                // await dispatch(checkTablesUploadStatus({ IO_NO: ioNo, REV_NO: revNo, flowName }));
                                                                onSuccess('Upload complete');
                                                        } else if (normFileType === 'project_milestone_mapping') {
                                                                const transformed = (data || []).map((row) => ({
                                                                        PID: row.PID ?? row.pid ?? row['PID'] ?? row['Project ID'] ?? '',
                                                                        MS_CODE: row.MS_CODE ?? row.ms_code ?? row['MS_CODE'] ?? row['Ms Code'] ?? '',
                                                                        SCHEDULE_DATE: row.SCHEDULE_DATE ?? row.schedule_date ?? row['SCHEDULE_DATE'] ?? row['Schedule Date'] ?? '',
                                                                        EXPECTED_DATE: row.EXPECTED_DATE ?? row.expected_date ?? row['EXPECTED_DATE'] ?? row['Expected Date'] ?? '',
                                                                        ACTUAL_DATE: row.ACTUAL_DATE ?? row.actual_date ?? row['ACTUAL_DATE'] ?? row['Actual Date'] ?? '',
                                                                        IO_NO: row.IO_NO ?? ioNo,
                                                                        REV_NO: row.REV_NO ?? revNo,
                                                                }));

                                                                await dispatch(uploadPmgProjectsBulk({ data: transformed, fileType }));
                                                                // await dispatch(checkTablesUploadStatus({ IO_NO: ioNo, REV_NO: revNo, flowName }));
                                                                onSuccess('Upload complete');
                                                        } else {
                                                                await dispatch(uploadExcelData(payload));
                                                                if (flowName === 'operatingPerformance') {
                                                                        openNotification('info', 'Calculate', `Please click on calculate button to update operating performance`);
                                                                }
                                                                await dispatch(checkTablesUploadStatus({ IO_NO: ioNo, REV_NO: revNo, flowName }));
                                                                onSuccess('Upload complete');
                                                        }
                                                } catch (error) {
                                                        openNotification('error', 'Something went wrong', error?.message || 'Error while processing the file.');
                                                }
                                        })();
                                }
                        }, 500);
                },
        });

        const formatFileSize = (size) => {
                if (size < 1024) return `${size} B`;
                if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
                return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        };

        let columns = [
                {
                        title: 'Title',
                        dataIndex: 'title',
                        key: 'title',
                        width: 300,
                        align: 'center',

                        onCell: () => ({
                                style: {
                                        color: '#475467',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        fontStyle: 'inter',
                                },
                        }),
                },

                {
                        title: 'Download CSV Template',
                        key: 'download',
                        width: 300,
                        // align: 'center',
                        onCell: () => ({
                                // style: { paddingLeft: '5rem' },
                        }),
                        render: (_, record) => (
                                <div className="flex flex-col gap-4 justify-between">
                                        <div className="flex justify-center">
                                                <Button
                                                        text="Download CSV "
                                                        icon={DownloadCsvIcon}
                                                        onClick={() => {
                                                                if (role === 'ADMIN') handleAdminFileDownload(record.fileName);
                                                                else handleDownload(record.fileName, 0);
                                                        }}
                                                />
                                        </div>
                                        {showVersionButtons && (
                                                <div className="flex justify-between cursor-pointer">
                                                        <div
                                                                className="underline text-[#0000FF] text-xs font-medium"
                                                                onClick={() => handleDownload(record.fileName, 'V1')}>
                                                                Version 1
                                                        </div>
                                                        <div
                                                                className="underline text-[#0000FF] text-xs font-medium"
                                                                onClick={() => handleDownload(record.fileName, 'V2')}>
                                                                Version 2
                                                        </div>
                                                </div>
                                        )}
                                </div>
                        ),
                },
                {
                        title: 'Upload CSV',
                        key: 'upload',
                        width: 400,
                        align: 'center',

                        render: (_, record) => {
                                const statusKey = statusKeyMap[record.fileName];
                                const isUploaded = uploadTablesStatus?.[statusKey];
                                const handleDisabledClick = (e) => {
                                        if (isAllFinalSubmitted) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                openNotification('error', 'Upload Disabled', 'It is already submitted. You cannot upload files now.');
                                        }
                                };

                                return (
                                        <div className="relative">
                                                {/* <div
                                                        onClick={handleDisabledClick}
                                                        onDrop={handleDisabledClick}
                                                        className={`absolute inset-0 z-10 ${isAllFinalSubmitted ? 'opacity-60 cursor-not-allowed pointer-events-auto' : 'pointer-events-none'}`}
                                                /> */}
                                                {isUploadLocked ? (
                                                        <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                                                <Lock
                                                                        size={38}
                                                                        className="text-red-500 mb-2"
                                                                />

                                                                <span className="text-sm font-semibold ">
                                                                        Locked
                                                                </span>
                                                        </div>
                                                ) : (
                                                        <Dragger
                                                                {...getUploadProps(record.key, record.fileName)}
                                                                showUploadList={false}
                                                                // disabled={isAllFinalSubmitted}
                                                                disabled={isAllFinalSubmitted || isUploadLocked}
                                                                prefixCls="border-none bg-white">
                                                                <div className={`flex flex-col items-start justify-center h-full relative bg-white border rounded-lg p-4 shadow-sm ${(isAllFinalSubmitted || isUploadLocked)
                                                                        ? "pointer-events-none opacity-60"
                                                                        : ""}
`}>
                                                                        {uploadedFiles[record.key] ? (
                                                                                <div className="w-full">
                                                                                        <div className="flex justify-between items-center w-full mb-2">
                                                                                                <div className="flex gap-4">
                                                                                                        <img
                                                                                                                src={csvIcon}
                                                                                                                alt="csv-icon"
                                                                                                        />
                                                                                                        <div className="flex flex-col">
                                                                                                                <p className="font-medium text-[#344054] font-inter text-sm">{uploadedFiles[record.key].name}</p>
                                                                                                                <p className="font-medium text-[#344054] font-inter text-sm">{formatFileSize(uploadedFiles[record.key].size)}</p>
                                                                                                        </div>
                                                                                                </div>
                                                                                                <div className="flex items-center gap-2">
                                                                                                        <DeleteOutlined
                                                                                                                className="text-gray-500 cursor-pointer text-lg"
                                                                                                                onClick={() => handleDelete(record.key)}
                                                                                                        />
                                                                                                </div>
                                                                                        </div>
                                                                                        <Progress
                                                                                                percent={uploadProgress[record.key]}
                                                                                                showInfo={true}
                                                                                                strokeColor="#845EC2"
                                                                                        />
                                                                                </div>
                                                                        ) : (
                                                                                <div className="flex flex-col items-center justify-center w-full">
                                                                                        {isUploaded && <CheckCircleFilled className="text-green-500 text-xl" />}
                                                                                        {!isUploaded && (
                                                                                                <img
                                                                                                        src={uploadIcon}
                                                                                                        alt="upload-icon"
                                                                                                        className="w-8 h-8 mb-2"
                                                                                                />
                                                                                        )}

                                                                                        <p>
                                                                                                <span className="text-[#6941C6] cursor-pointer font-semibold text-sm">Browse</span> <span className="text-[#475467] text-sm font-inter font-normal">or drag and drop</span>
                                                                                        </p>
                                                                                        <p className="text-[#475467] text-sm font-inter font-normal">CSV (max. Up to 50 MB)</p>
                                                                                </div>
                                                                        )}
                                                                </div>
                                                        </Dragger>
                                                )}
                                        </div>
                                );
                        },
                },
        ];

        useEffect(() => {
                dispatch(checkTablesUploadStatus({ IO_NO: ioNo, REV_NO: revNo, flowName }));
                dispatch(getProjectStatus({}));
        }, []);

        return (
                <>
                        {loading ? (
                                <Loader />
                        ) : (
                                <>
                                        {isUploadLocked && (
                                                <div className="mb-5 flex items-start gap-4 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
                                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100">
                                                                <Lock
                                                                        size={22}
                                                                        className="text-amber-600 "
                                                                />
                                                        </div>

                                                        <div>
                                                                <h3 className="text-sm font-semibold text-amber-900">
                                                                        Uploads are currently disabled
                                                                </h3>

                                                                <p className="mt-1 text-sm text-amber-700">
                                                                        Uploads are currently disabled by the administrator.
                                                                        Please contact your administrator for assistance.
                                                                </p>
                                                        </div>
                                                </div>
                                        )}

                                        {flowName !== 'project' && (
                                                <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50/70 flex items-center gap-3 shadow-sm">
                                                        <div className="relative flex h-3 w-3 flex-shrink-0">
                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                                                        </div>
                                                        <AlertCircle className="text-amber-600 flex-shrink-0 h-5 w-5" />
                                                        <span className="text-amber-800 text-sm font-medium leading-5">
                                                                <strong>Important Notice:</strong> Please input all financial figures in <strong>Lakhs (INR in Lakhs)</strong> when preparing data within the Excel templates.
                                                        </span>
                                                </div>
                                        )}

                                        <Table
                                                columns={columns}
                                                dataSource={tableData ?? defaultData}
                                                pagination={false}
                                                bordered
                                                className="custom-ant-table"
                                        />
                                </>
                        )}
                </>
        );
};

export default CsvUploadTable;
